import React, { Suspense, Component, ReactNode, useState, useEffect } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LazyLoadingFallback from './LazyLoadingFallback';
import { appChunkPreloader } from '../../services/appChunkPreloader';

interface LazyWrapperProps {
  children: React.ReactNode;
  fallbackMessage?: string;
  appName?: string;
  appId?: string; // Add appId to check if preloaded
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Simple Error Boundary for lazy-loaded components
 */
class LazyErrorBoundary extends Component<
  { children: ReactNode; appName?: string },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode; appName?: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => window.location.reload()}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="h6" gutterBottom>
              Failed to load {this.props.appName || 'application'}
            </Typography>
            <Typography variant="body2">
              There was an error loading the application components. This might be due to a 
              network issue or the application being unavailable.
            </Typography>
          </Alert>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
                {this.state.error.message}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper component that provides Suspense and Error Boundary for lazy-loaded components.
 * This ensures graceful loading and error handling for code-split routes.
 * 
 * For preloaded apps, shows a minimal loading state to improve perceived performance.
 */
const LazyWrapper: React.FC<LazyWrapperProps> = ({ 
  children, 
  fallbackMessage,
  appName,
  appId
}) => {
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [showMinimalLoading, setShowMinimalLoading] = useState(false);

  useEffect(() => {
    if (appId && appChunkPreloader.isPreloaded(appId)) {
      setIsPreloaded(true);
      // For preloaded apps, show minimal loading for just 100ms to avoid flash
      setShowMinimalLoading(true);
      const timer = setTimeout(() => setShowMinimalLoading(false), 100);
      return () => clearTimeout(timer);
    }
  }, [appId]);

  // Custom fallback for preloaded apps - much faster and less intrusive
  const PreloadedFallback = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '200px',
      opacity: 0.7
    }}>
      <Typography variant="body2" color="text.secondary">
        Loading {appName || 'application'}...
      </Typography>
    </Box>
  );

  return (
    <LazyErrorBoundary appName={appName}>
      <Suspense fallback={
        isPreloaded && showMinimalLoading ? 
          <PreloadedFallback /> : 
          <LazyLoadingFallback message={fallbackMessage} />
      }>
        {children}
      </Suspense>
    </LazyErrorBoundary>
  );
};

export default LazyWrapper;
